import { createFeatureSelector, createSelector } from '@ngrx/store';
import { selectQueryParams } from 'src/app/app.selectors';
import { Privileges } from 'src/app/privileges/privileges';
import { environment } from 'src/environments/environment';
import { lcsRestrictedPrivileges } from '../models/lcs-restricted-privileges.model';
import { AuthenticationState, authenticationFeatureKey } from './authentication.reducer';

const authenticationFeatureSelector = createFeatureSelector<AuthenticationState>(authenticationFeatureKey);

export const getBuildTimestamp = createSelector(authenticationFeatureSelector, (state) => {
   if (state.buildTimestamp) {
      return state.buildTimestamp;
   }
   return null;
});

export const getApplicationVersion = createSelector(authenticationFeatureSelector, (state) => {
   if (state.applicationVersion) {
      return state.applicationVersion;
   }
   return null;
});

// eslint-disable-next-line @typescript-eslint/naming-convention
export const getNdtVersion = createSelector(authenticationFeatureSelector, (state) => {
   if (state.ndtVersion) {
      return state.ndtVersion;
   }
   return null;
});

export const getCurrentUserId = createSelector(authenticationFeatureSelector, (state) => {
   if (state.currentUser) {
      return state.currentUser.userId;
   }
   return null;
});

export const getCurrentUser = createSelector(authenticationFeatureSelector, (state) => {
   return state.currentUser;
});

export const getCurrentUserIsSystemUser = createSelector(authenticationFeatureSelector, (state) => {
   return state.currentUser?.isSystemUser ?? false;
});

export const getCurrentUserIsAdminUser = createSelector(authenticationFeatureSelector, (state) => {
   return state.currentUser?.isAdmin ?? false;
});

export const getCurrentUserIsTrueSystemUser = createSelector(authenticationFeatureSelector, (state) => {
   return state.currentUser?.isTrueSystemUser ?? false;
});

export const getUsernameQueryParameter = createSelector(selectQueryParams, (queryParams) => {
   if (queryParams && queryParams.username) {
      return queryParams.username;
   }
   return '';
});

export const getIsLoggedIn = createSelector(authenticationFeatureSelector, (state) => {
   return (
      state.currentUser != null &&
      state.currentUser.userId > 0 &&
      state.tokenExpirationDate != null &&
      new Date(state.tokenExpirationDate).getTime() > new Date().getTime()
   );
});

export const getLoginCallState = createSelector(authenticationFeatureSelector, (state) => {
   return state.loginCallState;
});

export const selectLoginError = createSelector(authenticationFeatureSelector, (state) => {
   return state.loginError;
});

export const selectCurrentUserIsLocal = createSelector(authenticationFeatureSelector, (state) => {
   if (state.currentUser) {
      return state.currentUser.isLocal;
   }
   return null;
});

export const selectPrivileges = createSelector(authenticationFeatureSelector, (state) => {
   return state.privileges;
});

export function hasPrivilege(privilege: Privileges, accountId = 0) {
   return createSelector(selectPrivileges, (privileges: Map<number, Privileges[]>) => {
      return privileges.get(accountId)?.includes(privilege) ?? false;
   });
}

export function accountsToFilterOut(privilege: Privileges) {
   return createSelector(selectPrivileges, (privileges: Map<number, Privileges[]>) => {
      const idsToFilter: number[] = [];
      for (const key of privileges.keys()) {
         if (key > 0) {
            if (!privileges.get(key)?.includes(privilege)) {
               idsToFilter.push(key);
            }
         }
      }
      return idsToFilter;
   });
}

export function hasSelectedPrivilege(
   privilege: Privileges,
   accountId: number | null,
   privileges: Map<number | null, Privileges[]>
) {
   if (lcsRestrictedPrivileges.indexOf(privilege) >= 0 && accountId == environment.lcsAccountId) {
      return privileges.get(accountId)?.includes(privilege) ?? false;
   }
   return privileges.has(accountId)
      ? privileges.get(accountId)?.includes(privilege) ?? false
      : privileges.get(0)?.includes(privilege) ?? false;
}
